import {callApi} from '@/lib/api/callApi';
import {memoize} from 'lodash';

/**
 * 取得所有檢定
 * @param {Object} params 參數
 * @returns {Promise} promise
 */
async function getExams(params) {
  const res = await callApi({
    category: 'exam',
    apiName: '',
    method: 'get',
    params,
    defaultError: null,
  });
  return res.data;
}

/**
 * 報名檢定
 * @param {Object} data 參數
 * @returns {Promise} promise
 */
async function createOrder(data) {
  const res = await callApi({
    category: 'exam',
    apiName: 'order',
    method: 'post',
    data,
  });
  return res.data;
}

/**
 * 取得單一檢定
 * @param {String} examId 檢定id
 * @returns {Promise} promise
 */
async function getExamById(examId) {
  const res = await callApi({
    category: 'exam',
    apiName: examId,
    method: 'get',
    defaultError: null,
  });
  return res.data;
}

/**
 * 使用token取得檢定資料
 * @param {String} token 掃描qrcode核銷後取得的examJwt
 * @returns {Promise} promise
 */
async function getExamByToken() {
  const res = await callApi({
    category: 'exam',
    apiName: 'token',
    method: 'get',
  });
  return res.data;
}

/**
 * 更新檢定做題項目每一題的狀態
 * 開始作答時傳入examId、tsumeId、startAt
 * 作答完成後再傳入examId、tsumeId、isCorrect、answerSgf
 * @param {Object} params 參數
 * @param {String} params.examId 檢定id
 * @param {String} params.tsumeId 做題id，即questions中的id
 * @param {Boolean} params.isCorrect 該題是否正確
 * @param {String} params.answerSgf 該題使用者回答的sgf
 * @param {String} params.startAt 該題開始時間，毫秒
 * @returns {Promise} promise
 */
async function updateExamTsume(params) {
  const data = {
    isCorrect: params.isCorrect,
    answerSgf: params.answerSgf,
    startAt: params.startAt,
  };
  const res = await callApi({
    category: 'exam',
    apiName: `${params.examId}/tsume/${params.tsumeId}`,
    method: 'patch',
    data,
  });
  return res.data;
}

/**
 * 更新檢定下棋項目的狀態
 * @param {Object} params 參數
 * @param {String} params.examId 檢定id
 * @param {String} params.gameId 下棋id
 * @param {String} params.startAt 該題開始時間，毫秒
 * @returns {Promise} promise
 */
async function updateExamGame(params) {
  const data = {
    startAt: params.startAt,
  };
  const res = await callApi({
    category: 'exam',
    apiName: `${params.examId}/game/${params.gameId}`,
    method: 'patch',
    data,
  });
  return res.data;
}

/**
 * 紀錄使用者落子及時間
 * @param {Object} params 參數
 * @param {String} params.examId 檢定id
 * @param {String} params.gameId 下棋id
 * @param {String} params.boardMove 使用者落子
 * @param {Number} params.time 落子當下剩餘總時間
 * @param {Number} params.byoYomiTime 落子當下剩餘讀秒秒數
 * @param {Number} params.byoYomiAmount 落子當下剩餘讀秒次數
 * @returns {Promise} promise
 */
async function setPlayerMove(params) {
  const data = {
    boardMove: params.boardMove,
    time: params.time,
    byoYomiTime: params.byoYomiTime,
    byoYomiAmount: params.byoYomiAmount,
  };
  const res = await callApi({
    category: 'exam',
    apiName: `${params.examId}/game/${params.gameId}/move`,
    method: 'post',
    data,
  });
  return res.data;
}

/**
 * 取得ai落子
 * @param {Object} params 參數
 * @param {String} params.examId 檢定id
 * @param {String} params.gameId 下棋id
 * @param {Number} tryCount 嘗試次數，預設0
 * @returns {Promise} 會回傳新的ai剩餘總時數/讀秒秒數/讀秒次數，須更新至檢定房間前端畫面
 */
async function getAiMove(params, tryCount = 0) {
  try {
    const res = await callApi({
      category: 'exam',
      apiName: `${params.examId}/game/${params.gameId}/aiMove`,
      method: 'post',
      timeout: 9 * 1000,
      data: params,
    });
    return res.data;
  } catch (error) {
    if (error.response?.status === 504 && tryCount < 3) {
      // 如果是504 Gateway Timeout，重試最多3次
      params.isRetry = true; // 標記為重試
      return getAiMove(params, tryCount + 1);
    }
    // 其他錯誤直接拋出
    throw error;
  }
}

/**
 * 紀錄檢定下棋超時/同時後端也會做檢查
 * @param {Object} params 參數
 * @param {String} params.examId 檢定id
 * @param {String} params.gameId 下棋id
 * @returns {Promise} 檢定下棋對局結果
 */
async function setGameTimeout(params) {
  const res = await callApi({
    category: 'exam',
    apiName: `${params.examId}/game/${params.gameId}/timeout`,
    method: 'post',
  });
  return res.data;
}

/**
 * 需要算輸贏，取得當前對局算輸贏結果，前端須停止計時
 * @param {Object} params 參數
 * @param {String} params.examId 檢定id
 * @param {String} params.gameId 下棋id
 * @returns {Promise} {
  "isOver": 對局是否已結束,
  "influence": 未下完的位置,
  "winningWay": 對局結果,
  "winner": 對局輸贏}
 */
async function getJudgement(params) {
  const res = await callApi({
    category: 'exam',
    apiName: `${params.examId}/game/${params.gameId}/judgement`,
    method: 'get',
  });
  return res.data;
}

/**
 * 同意算輸贏
 * @param {Object} params 參數
 * @param {String} params.examId 檢定id
 * @param {String} params.gameId 下棋id
 * @returns {Promise} promise
 */
async function confirmJudgementResult(params) {
  const res = await callApi({
    category: 'exam',
    apiName: `${params.examId}/game/${params.gameId}/confirmJudgement`,
    method: 'post',
  });
  return res.data;
}

/**
 * 取消算輸贏，前端需開始計時
 * @param {Object} params 參數
 * @param {String} params.examId 檢定id
 * @param {String} params.gameId 下棋id
 * @returns {Promise} promise
 */
async function cancelJudgementResult(params) {
  const res = await callApi({
    category: 'exam',
    apiName: `${params.examId}/game/${params.gameId}/cancelJudgement`,
    method: 'post',
  });
  return res.data;
}

// 以下未確認是否還需使用

/**
 * 更新測評狀態
 * @param {String} examId 測評id
 * @param {String} data 修改內容
 * @returns {Promise} promise
 */
async function updateExamStatus(examId, data) {
  const res = await callApi({
    category: 'exam',
    apiName: examId,
    method: 'patch',
    data,
  });
  return res.data;
}

/**
 * 送出測評分數
 * @param {String} examId 測評id
 * @returns {Promise} promise
 */
async function setExamScore(examId) {
  const res = await callApi({
    category: 'exam',
    apiName: `${examId}/score`,
    method: 'post',
  });
  return res.data;
}

/**
 * 取得未完成測評
 * @returns {Promise} promise
 */
async function getUnfinishedExam() {
  const res = await callApi({
    category: 'exam',
    apiName: 'unfinished',
    method: 'get',
  });
  return res.data;
}

/**
 * 取得單一測評
 * @returns {Promise} promise
 */
async function getCalculatingExam() {
  const res = await callApi({
    category: 'exam',
    apiName: 'calculating',
    method: 'get',
  });
  return res.data;
}

/**
 * 取得單一測評
 * @param {String} examId 測評id
 * @param {Object} data 參數
 * @returns {Promise} promise
 */
async function createExamTsume(examId, data) {
  const res = await callApi({
    category: 'exam',
    apiName: `${examId}/tsume`,
    method: 'post',
    data,
  });
  return res.data;
}

/**
 * 取得單一測評
 * @param {Object} gameData 參數
 * @returns {Promise} promise
 */
async function createExamGame(gameData) {
  const res = await callApi({
    category: 'exam',
    apiName: `${gameData.examId}/game`,
    method: 'post',
    data: gameData.data,
  });
  return res.data;
}

/**
 * 取得單一測評
 * @returns {Promise} promise
 */
const getExamConfigs = memoize(async function getExamConfigs() {
  const res = await callApi({
    category: 'exam',
    apiName: 'configs',
    method: 'get',
  });
  return res.data;
});

/**
 * 取得單一測評
 * @param {Number} rank 參數
 * @returns {Promise} promise
 */
async function getExamQuestions(rank) {
  const res = await callApi({
    category: 'exam',
    apiName: `/questions/${rank}`,
    method: 'get',
  });
  return res.data;
}

/**
 * 取得該用戶通過了哪些 rank 的測評
 * @returns {Promise} promise
 */
async function getExamPass() {
  const res = await callApi({
    category: 'exam',
    apiName: 'pass',
    method: 'get',
  });
  return res.data;
}

/**
 * 行政人員通過驗證
 * @param {Object} data params
 * @returns {Promise} promise
 */
async function activeExam(data) {
  const res = await callApi({
    category: 'exam',
    apiName: 'active',
    method: 'post',
    data,
    defaultError: null,
  });
  return res.data;
}

/**
 * 取得檢定 Jwt
 * @param {Object} params params
 * @returns {Promise} promise
 */
async function getExamJwtToken(params) {
  const res = await callApi({
    category: 'exam',
    apiName: 'jwt',
    method: 'get',
    params,
    defaultError: null,
  });
  return res.data;
}

export default {
  createOrder,
  getExams,
  getExamById,
  getExamByToken,
  updateExamTsume,
  updateExamGame,
  updateExamStatus,
  setGameTimeout,
  setExamScore,
  getUnfinishedExam,
  getCalculatingExam,
  createExamTsume,
  createExamGame,
  setPlayerMove,
  getAiMove,
  getJudgement,
  confirmJudgementResult,
  cancelJudgementResult,
  getExamConfigs,
  getExamQuestions,
  getExamPass,
  activeExam,
  getExamJwtToken,
};
